import { useMemo } from 'react';
import type { Socio } from '../../types';
import { fmtBRL, fmtPct } from './formatadores';

export interface ItemDivisao {
  socio_id: number;
  socio_nome: string;
  pct_contratuais: number;
  pct_sucumbencia: number;
}

/** Divisão default = % padrão de cada sócio ativo. */
export function defaultDivisao(socios: Socio[]): ItemDivisao[] {
  return socios.filter((s) => s.ativo).map((s) => ({
    socio_id: s.id,
    socio_nome: s.nome,
    pct_contratuais: s.pct_contratuais,
    pct_sucumbencia: s.pct_sucumbencia,
  }));
}

export function somasDivisao(itens: ItemDivisao[]) {
  return {
    contratuais: itens.reduce((a, b) => a + (Number(b.pct_contratuais) || 0), 0),
    sucumbencia: itens.reduce((a, b) => a + (Number(b.pct_sucumbencia) || 0), 0),
  };
}

/** Sem sócios → divisão vazia é válida; com sócios, as duas somas devem fechar 100. */
export function divisaoValida(itens: ItemDivisao[]): boolean {
  if (!itens.length) return true;
  const s = somasDivisao(itens);
  return Math.abs(s.contratuais - 100) < 0.01 && Math.abs(s.sucumbencia - 100) < 0.01;
}

interface Props {
  socios: Socio[];
  itens: ItemDivisao[];
  valorContratuais: number;
  valorSucumbencia: number;
  onChange: (itens: ItemDivisao[]) => void;
}

export function DivisaoSociosEditor({ socios, itens, valorContratuais, valorSucumbencia, onChange }: Props) {
  const somas = useMemo(() => somasDivisao(itens), [itens]);
  const okC = Math.abs(somas.contratuais - 100) < 0.01;
  const okS = Math.abs(somas.sucumbencia - 100) < 0.01;

  function set(i: number, campo: 'pct_contratuais' | 'pct_sucumbencia', v: number) {
    onChange(itens.map((it, idx) => (idx === i ? { ...it, [campo]: v } : it)));
  }

  if (!socios.length) {
    return (
      <div className="text-xs text-amber-600 dark:text-amber-400">
        Cadastre os sócios na <span className="font-medium">Administração → Sócios &amp; honorários</span> para dividir os honorários.
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Divisão entre sócios</span>
        <button type="button" onClick={() => onChange(defaultDivisao(socios))}
          className="text-[11px] text-blue-600 dark:text-blue-400 hover:underline">restaurar padrão</button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400">
            <tr>
              <th className="text-left px-2 py-1.5 font-medium">Sócio</th>
              <th className="px-2 py-1.5 font-medium w-24">% contrat.</th>
              <th className="px-2 py-1.5 font-medium w-24">% sucumb.</th>
              <th className="text-right px-2 py-1.5 font-medium">Valor</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((it, i) => {
              const vc = (valorContratuais * (Number(it.pct_contratuais) || 0)) / 100;
              const vs = (valorSucumbencia * (Number(it.pct_sucumbencia) || 0)) / 100;
              return (
                <tr key={it.socio_id} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="px-2 py-1.5 text-slate-700 dark:text-slate-200">{it.socio_nome}</td>
                  <td className="px-2 py-1"><InputPct value={it.pct_contratuais} onChange={(v) => set(i, 'pct_contratuais', v)} /></td>
                  <td className="px-2 py-1"><InputPct value={it.pct_sucumbencia} onChange={(v) => set(i, 'pct_sucumbencia', v)} /></td>
                  <td className="px-2 py-1.5 text-right tabular-nums text-slate-700 dark:text-slate-200">{fmtBRL(vc + vs)}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
              <td className="px-2 py-1.5 text-slate-500 dark:text-slate-400">Soma</td>
              <td className={`px-2 py-1.5 text-center font-semibold ${okC ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                {fmtPct(somas.contratuais)} {okC ? '✓' : '⚠'}
              </td>
              <td className={`px-2 py-1.5 text-center font-semibold ${okS ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                {fmtPct(somas.sucumbencia)} {okS ? '✓' : '⚠'}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      {(!okC || !okS) && (
        <p className="mt-1 text-[11px] text-rose-600 dark:text-rose-400">As duas somas precisam fechar 100% para salvar.</p>
      )}
    </div>
  );
}

function InputPct({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <input
      type="number" min={0} max={100} step="0.01" value={value}
      onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
      className="w-full px-1.5 py-1 text-right tabular-nums rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-400"
    />
  );
}
